import Input from '../../../../components/Input';
import Select from '../../../../components/Select';
import type { ChangeEvent, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdClear } from 'react-icons/md';
import MultiSelect from '../../../../components/MultiSelect';
import MultiSelectManageLink from '../../../../components/MultiSelectManageLink';
import CompanySelect from './CompanySelect';
import MultiSelectPopover from './MultiSelectPopover';
import useProvidedJobPostTags from '../hooks/useProvidedJobPostTags';
import useProvidedJobPostLists from '../../provided_job_post_lists/api/useProvidedJobPostLists';
import { useController, useFormContext, Controller } from 'react-hook-form';
import type { FormValue } from '../searchSchema';
import Checkbox from '../../../../components/Checkbox';
import { useDebounce } from '@/hooks/useDebounce';
import { useRef } from 'react';
import JobSourceList from './JobSourceList';
import CustomTagMatchMenu from '@/components/CustomTagMatchMenu';
import useFetchFlattenJobSources from '../../api/useFetchJobSources';
import usePrefectureSelect from '../hooks/usePrefectureSelect';

// 求人管理ページ用のソートオプション
export const orderItems = [
  { value: 'updated_at:desc', label: '更新が新しい順' },
  { value: 'updated_at:asc', label: '更新が古い順' },
  { value: 'last_crawled_at:desc', label: '外部連携が新しい順' },
  { value: 'created_at:desc', label: '作成が新しい順' },
  { value: 'created_at:asc', label: '作成が古い順' },
];

// デフォルトのソート値
export const DEFAULT_SORT = 'updated_at:desc';

// ソート値が有効か判定し、無効なら既定値を返す
export const normalizeSort = (value: string | undefined | null): string => {
  if (!value) return DEFAULT_SORT;
  return orderItems.some((item) => item.value === value) ? value : DEFAULT_SORT;
};

const categoryItems = [
  { value: 'title', label: '求人タイトル' },
  { value: 'mandatory_requirement', label: '必須要件' },
  { value: 'keyword_content', label: '求人内容' },
  { value: 'memo', label: '社内メモ' },
];

const createdTypeItems = [
  { value: 'manual', label: '手動作成' },
  { value: 'outer_job_post', label: '連携アカウント経由' },
  { value: 'shared_job_post', label: '求人データベース' },
];

const conditionItems = [
  { value: 'or', label: 'のいずれかを含む(OR)' },
  { value: 'and', label: 'を全て含む(AND)' },
  { value: 'not_or', label: 'のいずれかを含まない(NOT/OR)' },
  { value: 'not_and', label: 'をすべて含まない(NOT/AND)' },
];

interface Props {
  onSubmit: (data: FormValue) => void;
  // 「検索条件をクリア」ボタンの左隣に差し込む追加アクション（例: 全件選択ボタン）
  clearButtonLeftSlot?: ReactNode;
}

export default function SearchForm({ onSubmit, clearButtonLeftSlot }: Props) {
  const debounce = useDebounce(500);
  const [searchJobSource, setSearchJobSource] = useState('');
  const [jobSourceName, setJobSourceName] = useState('');
  const [isJobSourceFocus, setIsJobSourceFocus] = useState(false);
  const { data: jobSources } = useFetchFlattenJobSources({ query: searchJobSource });
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const { data } = useProvidedJobPostTags();
  const { data: lists } = useProvidedJobPostLists();
  const isInitialMount = useRef(true);
  const searchDebounce = useDebounce(300);

  const { register, watch, handleSubmit, control, setValue, reset } = useFormContext<FormValue>();

  const toHalfWidthDigits = (value: string) =>
    value.replace(/[０-９]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0xfee0));

  const registerNumber = (name: Parameters<typeof register>[0]) => {
    const field = register(name);
    return {
      ...field,
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        e.target.value = toHalfWidthDigits(e.target.value);
        return field.onChange(e);
      },
    };
  };

  const isIncludeArchivedController = useController({ name: 'isIncludeArchived', control });
  const isSalaryEmptyController = useController({ name: 'isSalaryEmpty', control });
  const isSalaryEmpty = watch('isSalaryEmpty');
  const customTagIds = watch('customTagIds');
  const customTagIdsController = useController({ name: 'customTagIds', control });
  const customTagMatchController = useController({ name: 'customTagMatch', control });
  const listIdController = useController({ name: 'listId', control });
  const sortController = useController({ name: 'sort', control });

  const { prefectureNames, prefectureOptions, handlePrefectureUpdate } = usePrefectureSelect();

  const onSelectJobSource = (id: number, name: string) => {
    setValue('jobSourceId', id);
    setJobSourceName(name);
  };

  const onInputJobSource = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setJobSourceName(value);
    // 手入力で書き換えた時点で選択済みの絞り込みを解除する（入力欄を消しても条件が残る不整合を防ぐ）
    setValue('jobSourceId', undefined);

    debounce(async () => {
      setSearchJobSource(value);
    });
  };

  // URL / localStorage から復元した jobSourceId に対応する連携先名を入力欄へ反映する
  const jobSourceId = watch('jobSourceId');
  useEffect(() => {
    if (jobSourceId === undefined) return;
    if (jobSourceName !== '') return;
    const matched = jobSources?.find((jobSource) => jobSource.id === jobSourceId);
    if (matched) setJobSourceName(matched.title);
  }, [jobSourceId, jobSourceName, jobSources]);

  // 自動検索機能 - フォームの変更を監視（デバウンス付き）
  useEffect(() => {
    // 初回レンダリング時はスキップ
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const subscription = watch(() => {
      searchDebounce(() => {
        handleSubmit(onSubmit)();
      });
    });

    return () => subscription.unsubscribe();
  }, [watch, handleSubmit, onSubmit, searchDebounce]);

  // タグ選択用のオプション
  const tagOptions =
    data?.tags?.map((tag) => ({
      id: tag.id,
      name: tag.name,
      colorHex: tag.colorHex,
    })) ?? [];

  // 検索条件をクリアする関数
  const handleClearSearch = () => {
    // デフォルト値にリセット
    reset({
      companyIds: [],
      salary: '',
      minSalary: '',
      maxSalary: '',
      jobPositions: '',
      prefectureCodes: [],
      customTagIds: [],
      customTagMatch: 'all',
      listId: undefined,
      searchColumnAlpha: 'title',
      searchKeywordAlpha: '',
      searchOperatorAlpha: 'or',
      searchColumnBeta: 'mandatory_requirement',
      searchKeywordBeta: '',
      searchOperatorBeta: 'or',
      searchColumnGamma: 'keyword_content',
      searchKeywordGamma: '',
      searchOperatorGamma: 'or',
      isIncludeArchived: false,
      isSalaryEmpty: false,
      sort: 'updated_at:desc',
      jobSourceId: undefined,
      createdType: undefined,
    });

    // ローカルステートもリセット
    setSearchJobSource('');
    setJobSourceName('');
    setIsDetailOpen(false);

    // 検索を実行
    onSubmit({
      companyIds: [],
      salary: '',
      minSalary: '',
      maxSalary: '',
      jobPositions: '',
      prefectureCodes: [],
      customTagIds: [],
      customTagMatch: 'all',
      listId: undefined,
      searchColumnAlpha: 'title',
      searchKeywordAlpha: '',
      searchOperatorAlpha: 'or',
      searchColumnBeta: 'mandatory_requirement',
      searchKeywordBeta: '',
      searchOperatorBeta: 'or',
      searchColumnGamma: 'keyword_content',
      searchKeywordGamma: '',
      searchOperatorGamma: 'or',
      isIncludeArchived: false,
      isSalaryEmpty: false,
      sort: 'updated_at:desc',
      jobSourceId: undefined,
      createdType: undefined,
    });
  };

  // 検索条件が設定されているかチェック
  const hasSearchConditions = () => {
    const currentValues = watch();

    // 各フィールドの値をチェック
    if (currentValues.companyIds && currentValues.companyIds.length > 0) return true;
    if (currentValues.salary && currentValues.salary.trim() !== '') return true;
    if (currentValues.minSalary && currentValues.minSalary.trim() !== '') return true;
    if (currentValues.maxSalary && currentValues.maxSalary.trim() !== '') return true;
    if (currentValues.jobPositions && currentValues.jobPositions.trim() !== '') return true;
    if (currentValues.prefectureCodes && currentValues.prefectureCodes.length > 0) return true;
    if (currentValues.customTagIds && currentValues.customTagIds.length > 0) return true;
    if (currentValues.listId) return true;
    if (currentValues.searchKeywordAlpha && currentValues.searchKeywordAlpha.trim() !== '')
      return true;
    if (currentValues.searchKeywordBeta && currentValues.searchKeywordBeta.trim() !== '')
      return true;
    if (currentValues.searchKeywordGamma && currentValues.searchKeywordGamma.trim() !== '')
      return true;
    if (currentValues.isIncludeArchived === true) return true;
    if (currentValues.isSalaryEmpty === true) return true;
    if (currentValues.jobSourceId !== undefined) return true;
    if (currentValues.createdType !== undefined) return true;
    if (searchJobSource.trim() !== '' || jobSourceName.trim() !== '') return true;

    return false;
  };

  return (
    <>
      {/* 検索フォーム本体 */}
      <div className="w-full rounded bg-white p-5 shadow-layer1">
        <div className="space-y-5">
          {/* 1段目: 企業名, 年収 */}
          <div className="flex flex-wrap gap-lg">
            {/* 企業名検索 */}
            <div className="flex w-[426px] max-w-full min-w-0 flex-col gap-2">
              <label className="text-sm font-bold">企業名</label>
              <CompanySelect onSetCompanyIds={() => {}} includeSharedJobPost />
            </div>

            {/* 年収検索 */}
            <div className="flex min-w-0 flex-col gap-2">
              <label className="text-sm font-bold">年収</label>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
                  <Input
                    {...registerNumber('salary')}
                    placeholder="0"
                    className="w-[130px]"
                    disabled={isSalaryEmpty}
                  />
                  <span className="text-sm">万円を含む</span>
                </div>
                <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
                  <Input
                    {...registerNumber('minSalary')}
                    placeholder="0"
                    className="w-[130px]"
                    disabled={isSalaryEmpty}
                  />
                  <span className="text-sm">〜</span>
                  <Input
                    {...registerNumber('maxSalary')}
                    placeholder="0"
                    className="w-[130px]"
                    disabled={isSalaryEmpty}
                  />
                  <span className="text-sm">万円</span>
                </div>
                <Checkbox
                  checked={isSalaryEmptyController.field.value}
                  onChange={(v) => isSalaryEmptyController.field.onChange(v)}
                >
                  <span className="text-sm font-normal">年収データなし</span>
                </Checkbox>
              </div>
            </div>
          </div>

          {/* 2段目: ポジション, 勤務地 */}
          <div className="flex flex-wrap gap-5">
            {/* ポジション検索（フリーワード、空白区切りで OR 部分一致） */}
            <div className="flex w-[426px] max-w-full min-w-0 flex-col gap-2">
              <label className="text-sm font-bold">ポジション</label>
              <Input
                search
                placeholder="ポジションで絞り込む"
                {...register('jobPositions')}
                className="w-full"
              />
            </div>

            {/* 勤務地検索 */}
            <div className="flex w-[365px] max-w-full min-w-0 flex-col gap-2">
              <label className="text-sm font-bold">勤務地</label>
              <MultiSelectPopover
                values={prefectureNames}
                placeholder="勤務地を選択"
                options={prefectureOptions}
                onUpdate={handlePrefectureUpdate}
                searchable
                align="end"
              />
            </div>
          </div>

          {/* 3段目: タグ・リスト */}
          <div className="flex flex-wrap gap-5">
            <div className="flex min-w-[280px] flex-1 flex-col gap-xs">
              <label className="text-heading-xxs">タグ</label>
              <MultiSelect
                options={tagOptions}
                value={customTagIds}
                onChange={(values) => {
                  customTagIdsController.field.onChange(values);
                }}
                placeholder="タグを選択"
                variant="list"
                header={
                  <CustomTagMatchMenu
                    value={customTagMatchController.field.value}
                    onChange={customTagMatchController.field.onChange}
                  />
                }
                footer={
                  <MultiSelectManageLink to="/agent/job_post_tags">
                    タグを管理
                  </MultiSelectManageLink>
                }
              />
            </div>
            <div className="flex min-w-[280px] flex-1 flex-col gap-xs">
              <label className="text-heading-xxs">リスト</label>
              <Select
                key={listIdController.field.value ?? '__empty__'}
                items={
                  lists?.map((list) => ({
                    value: String(list.id),
                    label: list.name,
                  })) ?? []
                }
                value={listIdController.field.value}
                onValueChange={listIdController.field.onChange}
                defaultLabel="リストを選択"
                footer={
                  <MultiSelectManageLink to="/agent/provided_job_post_lists">
                    リストを管理
                  </MultiSelectManageLink>
                }
              />
            </div>
          </div>

          {/* 4段目: 掲載終了した求人も含める */}
          <div className="flex">
            <Checkbox
              checked={isIncludeArchivedController.field.value}
              onChange={(v) => isIncludeArchivedController.field.onChange(v)}
            >
              <span className="text-sm font-normal">掲載終了した求人も含める</span>
            </Checkbox>
          </div>

          {/* 詳細検索アコーディオンボタン */}
          <div className="flex items-center">
            <Button
              color="tertiary"
              size="md"
              type="button"
              onClick={() => setIsDetailOpen(!isDetailOpen)}
            >
              {isDetailOpen ? (
                <MdKeyboardArrowUp className="size-icon-xs text-primary" />
              ) : (
                <MdKeyboardArrowDown className="size-icon-xs text-primary" />
              )}
              <span className="text-sm font-bold text-primary">
                {isDetailOpen ? '詳細条件を閉じる' : '詳細条件を開く'}
              </span>
            </Button>
          </div>

          {/* 詳細検索アコーディオンコンテンツ */}
          {isDetailOpen && (
            <div className="space-y-5 rounded py-4">
              {/* 1段目: 取り込み元で絞り込む */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-bold">取り込み元で絞り込む</label>
                <div className="relative w-full lg:w-[417px]">
                  <Input
                    className="w-full"
                    placeholder="連携先を入力"
                    autoComplete="off"
                    onChange={onInputJobSource}
                    value={jobSourceName}
                    onFocus={() => setIsJobSourceFocus(true)}
                    onBlur={() => setTimeout(() => setIsJobSourceFocus(false), 200)}
                  />
                  {isJobSourceFocus && (
                    <div className="absolute top-11 z-50">
                      <JobSourceList
                        lists={jobSources ?? []}
                        onSelectJobSource={onSelectJobSource}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* 2段目: 作成方法で絞り込む */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-bold">作成方法で絞り込む</label>
                <div className="w-full lg:w-[417px]">
                  <Controller
                    name="createdType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        items={createdTypeItems}
                        value={field.value}
                        onValueChange={field.onChange}
                        defaultLabel="すべて"
                      />
                    )}
                  />
                </div>
              </div>

              {/* 3段目: 詳細キーワード設定 */}
              <div className="flex flex-col gap-3">
                <label className="text-sm font-bold">詳細キーワード設定で絞り込む</label>

                {/* Alpha検索 */}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="w-[200px]">
                    <Controller
                      name="searchColumnAlpha"
                      control={control}
                      render={({ field }) => (
                        <Select
                          items={categoryItems}
                          value={field.value}
                          onValueChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                  <span className="text-sm">に</span>
                  <Input
                    placeholder="キーワード（30文字以内）"
                    {...register('searchKeywordAlpha')}
                    className="w-[330px]"
                  />
                  <div className="w-[264px]">
                    <Controller
                      name="searchOperatorAlpha"
                      control={control}
                      render={({ field }) => (
                        <Select
                          items={conditionItems}
                          value={field.value}
                          onValueChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Beta検索 */}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex w-[200px] flex-col gap-2">
                    <Controller
                      name="searchColumnBeta"
                      control={control}
                      render={({ field }) => (
                        <Select
                          items={categoryItems}
                          value={field.value}
                          onValueChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                  <span className="text-sm">に</span>
                  <Input
                    placeholder="キーワード（30文字以内）"
                    {...register('searchKeywordBeta')}
                    className="w-[330px]"
                  />
                  <div className="w-[264px]">
                    <Controller
                      name="searchOperatorBeta"
                      control={control}
                      render={({ field }) => (
                        <Select
                          items={conditionItems}
                          value={field.value}
                          onValueChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Gamma検索 */}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="w-[200px]">
                    <Controller
                      name="searchColumnGamma"
                      control={control}
                      render={({ field }) => (
                        <Select
                          items={categoryItems}
                          value={field.value}
                          onValueChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                  <span className="text-sm">に</span>
                  <Input
                    placeholder="キーワード（30文字以内）"
                    {...register('searchKeywordGamma')}
                    className="w-[330px]"
                  />
                  <div className="w-[264px]">
                    <Controller
                      name="searchOperatorGamma"
                      control={control}
                      render={({ field }) => (
                        <Select
                          items={conditionItems}
                          value={field.value}
                          onValueChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ソート + 検索条件クリア */}
      <div className="mt-lg flex items-end justify-between gap-4">
        <div className="flex w-[196px] shrink-0 flex-col gap-2">
          <Select
            key={sortController.field.value ?? '__empty__'}
            className="w-full"
            value={normalizeSort(sortController.field.value)}
            onValueChange={sortController.field.onChange}
            items={orderItems}
            defaultLabel="並び順を選択"
          />
        </div>
        <div className="flex items-center gap-4">
          {clearButtonLeftSlot}
          {hasSearchConditions() && (
            <Button color="tertiary" size="md" type="button" onClick={handleClearSearch}>
              <MdClear className="size-icon-xs text-primary" />
              <span className="text-sm font-bold text-primary">検索条件をクリア</span>
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
